import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { PresentationChartLineIcon } from '@heroicons/react/24/outline';
import api from '../../api/client';

// Owner command center — center-wide operational + financial strength.
// Reads GET /api/center-metrics (director-only). Metrics are grouped by area;
// each card shows the latest value vs target, a status color, and a mini trend.

const STATUS = {
  green: 'bg-green-500', amber: 'bg-amber-400', red: 'bg-red-500', neutral: 'bg-blue-400', unknown: 'bg-gray-300',
};
const STATUS_TEXT = {
  green: 'text-green-700', amber: 'text-amber-700', red: 'text-red-700', neutral: 'text-gray-900', unknown: 'text-gray-400',
};
// Preferred display order for the area sections.
const AREA_ORDER = ['Operational', 'Financial', 'Parent Experience', 'Growth / CRM', 'Growth', 'People'];

function fmtValue(m) {
  if (m.value === null || m.value === undefined) return '—';
  if (m.unit === '$') return `$${Math.round(m.value).toLocaleString()}`;
  if (m.unit === '%') return `${Math.round(m.value * 10) / 10}%`;
  const v = Math.round(m.value * 10) / 10;
  return m.unit ? `${v} ${m.unit}` : `${v}`;
}

function Sparkline({ history }) {
  const pts = (history || []).map((h) => (h.value == null ? 0 : Number(h.value)));
  if (pts.length < 2) return null;
  const max = Math.max(...pts, 1);
  return (
    <div className="flex items-end gap-0.5 h-8 mt-2">
      {pts.map((v, i) => (
        <div key={i} className="w-1.5 bg-gray-200 rounded-sm" style={{ height: `${Math.max(6, (v / max) * 100)}%` }} title={`${v}`} />
      ))}
    </div>
  );
}

function MetricCard({ m }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-gray-800">{m.name}</p>
          {m.target != null && <p className="text-[11px] text-gray-400">Target: {m.target}{m.unit === '%' ? '%' : ''}</p>}
        </div>
        <span className={`mt-1 w-3 h-3 rounded-full ${STATUS[m.status] || STATUS.unknown}`} title={m.status} />
      </div>
      <p className={`text-3xl font-bold mt-2 ${STATUS_TEXT[m.status] || 'text-gray-900'}`}>{fmtValue(m)}</p>
      {m.metadata?.explanation && <p className="text-xs text-gray-500 mt-1">{m.metadata.explanation}</p>}
      <Sparkline history={m.history} />
      {m.period_start && (
        <p className="text-[11px] text-gray-400 mt-2">
          {String(m.period_start).slice(0, 10)}
          {m.period_end && m.period_end !== m.period_start ? ` – ${String(m.period_end).slice(0, 10)}` : ''}
        </p>
      )}
      <p className="text-[11px] text-gray-400 mt-1">{m.description}</p>
    </div>
  );
}

export default function OwnerCommandCenterPage() {
  const [areas, setAreas] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/center-metrics');
      setAreas(data.areas || {});
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load command center.');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const areaNames = Object.keys(areas).sort(
    (a, b) => (AREA_ORDER.indexOf(a) + 1 || 99) - (AREA_ORDER.indexOf(b) + 1 || 99)
  );
  const hasAny = areaNames.some((a) => areas[a]?.length);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6 animate-slide-up">
      <div className="flex items-center gap-2">
        <PresentationChartLineIcon className="h-7 w-7 text-orange-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Command Center</h1>
          <p className="text-sm text-gray-500">Center-wide operational + financial strength, across booking and portal.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : !hasAny ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center text-gray-400 text-sm">
          No metrics yet — they populate as booking pushes computed KPIs.
        </div>
      ) : (
        areaNames.map((area) => (
          <div key={area}>
            <h2 className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">{area}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {(areas[area] || []).map((m) => <MetricCard key={m.metric_key} m={m} />)}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
